import axiosInstance from 'src/config/axios.config';
import { API_ENDPOINTS } from 'src/config/api.config';

// Interface definitions
export interface DeviceChangeRequest {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  reasonType: DeviceChangeReasonType;
  reasonText: string | null;
  oldDeviceId: string | null;
  status: DeviceChangeRequestStatus;
  createdAt: string;
  reviewedBy: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

export enum DeviceChangeReasonType {
  NEW_DEVICE = 'NEW_DEVICE',
  DEVICE_BROKEN = 'DEVICE_BROKEN',
  DEVICE_LOST = 'DEVICE_LOST',
  DEVICE_SOLD = 'DEVICE_SOLD',
  APP_REINSTALL = 'APP_REINSTALL',
  OTHER = 'OTHER',
}

export enum DeviceChangeRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export interface DeviceChangeReviewRequest {
  status: DeviceChangeRequestStatus;
  notes?: string;
}

export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// Translation maps for display
export const REASON_TYPE_LABELS: Record<DeviceChangeReasonType, string> = {
  [DeviceChangeReasonType.NEW_DEVICE]: 'Yeni Cihaz Aldı',
  [DeviceChangeReasonType.DEVICE_BROKEN]: 'Cihaz Bozuldu',
  [DeviceChangeReasonType.DEVICE_LOST]: 'Cihaz Kayboldu/Çalındı',
  [DeviceChangeReasonType.DEVICE_SOLD]: 'Cihaz Satıldı/Verildi',
  [DeviceChangeReasonType.APP_REINSTALL]: 'Uygulama Yeniden Yüklendi',
  [DeviceChangeReasonType.OTHER]: 'Diğer',
};

export const STATUS_LABELS: Record<DeviceChangeRequestStatus, string> = {
  [DeviceChangeRequestStatus.PENDING]: 'Beklemede',
  [DeviceChangeRequestStatus.APPROVED]: 'Onaylandı',
  [DeviceChangeRequestStatus.REJECTED]: 'Reddedildi',
};

export const STATUS_COLORS: Record<DeviceChangeRequestStatus, 'warning' | 'success' | 'error'> = {
  [DeviceChangeRequestStatus.PENDING]: 'warning',
  [DeviceChangeRequestStatus.APPROVED]: 'success',
  [DeviceChangeRequestStatus.REJECTED]: 'error',
};

// Device Change Request API functions
export const deviceChangeRequestApi = {
  // Get all device change requests with pagination
  async getAllRequests(
    page: number = 0,
    size: number = 20,
    sortBy: string = 'createdAt',
    sortDirection: string = 'desc',
    status?: DeviceChangeRequestStatus
  ): Promise<PagedResponse<DeviceChangeRequest>> {
    const response = await axiosInstance.get<PagedResponse<DeviceChangeRequest>>(
      API_ENDPOINTS.DEVICE_CHANGE_REQUESTS,
      {
        params: {
          page,
          size,
          sortBy,
          sortDirection,
          status,
        },
      }
    );
    return response.data;
  },

  // Get pending count for dashboard
  async getPendingCount(): Promise<number> {
    const response = await axiosInstance.get<{ pendingCount: number }>(
      `${API_ENDPOINTS.DEVICE_CHANGE_REQUESTS}/pending-count`
    );
    return response.data.pendingCount;
  },

  // Get a specific request by ID
  async getRequestById(requestId: string): Promise<DeviceChangeRequest> {
    const response = await axiosInstance.get<DeviceChangeRequest>(
      `${API_ENDPOINTS.DEVICE_CHANGE_REQUESTS}/${requestId}`
    );
    return response.data;
  },

  // Get requests for a specific employee
  async getEmployeeRequests(employeeId: string): Promise<DeviceChangeRequest[]> {
    const response = await axiosInstance.get<DeviceChangeRequest[]>(
      `${API_ENDPOINTS.DEVICE_CHANGE_REQUESTS}/employee/${employeeId}`
    );
    return response.data;
  },

  // Review a device change request (approve or reject)
  async reviewRequest(
    requestId: string,
    request: DeviceChangeReviewRequest
  ): Promise<DeviceChangeRequest> {
    const response = await axiosInstance.put<DeviceChangeRequest>(
      `${API_ENDPOINTS.DEVICE_CHANGE_REQUESTS}/${requestId}`,
      request
    );
    return response.data;
  },
};
